import { useState, useEffect, useCallback } from "react";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { musicEndpoints } from "../utilities/musicEndpoints";
import {
  ISpotifySearchResponse,
  ISpotifyTrack,
} from "../models/musicSuggestionModels";

export const useMusicSearch = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce the search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch search results from Deezer API
  const { data, isLoading, error } = useApiQuery<ISpotifySearchResponse>({
    queryKey: ["musicSearch", debouncedQuery],
    endpoint: `${musicEndpoints.searchTracks()}?q=${encodeURIComponent(debouncedQuery)}&limit=15`,
    enabled: debouncedQuery.length >= 2,
    globalIsLoading: false,
    disableErrorToast: true,
    retry: false,
  });

  const tracks = data?.tracks || [];

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setDebouncedQuery("");
  }, []);

  return {
    searchQuery,
    tracks,
    isLoading,
    error,
    handleSearch,
    clearSearch,
    hasResults: tracks.length > 0,
    showResults: debouncedQuery.length >= 2,
  };
};
